<template>
  <div>
    <DxDataGrid ref="dataGridRef" :data-source="dsParametri" key-expr="ID" :allow-column-reordering="true"
      :allow-column-resizing="true" column-resizing-mode="nextColumn" :show-borders="true" :show-row-lines="true"
      :show-column-lines="false" :column-auto-width="true" class="grid-border" @row-removing="onRowRemoving">
      <DxSelection mode="single" />
      <DxFilterRow :visible="true" />
      <DxPaging :page-size="20" />
      <DxSorting mode="multiple" />
      <DxStateStoring :enabled="true" storage-key="GridERP_Parametri" />
      <DxLoadPanel :enabled="true" />

      <DxEditing mode="cell" :allow-updating="false" :allow-adding="false" :allow-deleting="false" />

      <DxGridToolbar>
        <DxGridToolbarItem location="after">
          <template #default>
            <DxButton icon="plus" type="success" hint="Adaugă parametru" @click="onAddClick" />
          </template>
        </DxGridToolbarItem>
        <DxGridToolbarItem location="after">
          <template #default>
            <DxButton icon="refresh" hint="Reîncarcă" @click="onRefreshClick" />
          </template>
        </DxGridToolbarItem>
        <DxGridToolbarItem name="searchPanel" />
      </DxGridToolbar>

      <DxSearchPanel :visible="true" placeholder="Caută..." />

      <DxColumn data-field="Cod" caption="Cod" :width="200" />
      <DxColumn data-field="Nume" caption="Denumire" />
      <DxColumn data-field="Explicatie" caption="Explicație" :width="180" />
      <DxColumn data-field="Valoare" caption="Valoare" :width="250" />

      <DxColumn :width="110" cell-template="gridButtonsTemplate" caption="Acțiuni" alignment="center" />
      <template #gridButtonsTemplate="{ data }">
        <div class="grid-action-buttons">
          <i class="dx-icon-edit grid-btn-edit" title="Editează" @click="onEditClick(data.data)"></i>
          <i class="dx-icon-trash grid-btn-delete" title="Șterge" @click="confirmDelete(data.data)"></i>
        </div>
      </template>
    </DxDataGrid>

    <DxPopup v-model:visible="isModalVisible" :close-on-outside-click="false" :show-close-button="false"
      :show-title="true" :width="500" :height="380" :drag-enabled="true">
      <template #title>
        <div class="popup-header">
          <div class="popup-header-main">
            <span class="popup-header-icon" :class="isEditMode ? 'dx-icon-edit' : 'dx-icon-add'"></span>
            <div class="popup-header-texts">
              <div class="popup-header-title">{{ isEditMode ? 'Editare Parametru' : 'Adăugare Parametru' }}</div>
              <div class="popup-header-subtitle"></div>
            </div>
          </div>
          <div class="popup-header-meta">
            <span class="status-chip status-chip--draft">
              {{ isEditMode ? 'Editare' : 'Nou' }}
            </span>
          </div>
        </div>
      </template>

      <div class="popup-body-modern">
        <DxForm ref="formRef" :form-data="formData" label-mode="floating" :col-count="1">
          <DxSimpleItem data-field="Cod">
            <DxRequiredRule message="Codul este obligatoriu" />
          </DxSimpleItem>

          <DxSimpleItem data-field="Nume">
            <DxRequiredRule message="Denumirea este obligatorie" />
          </DxSimpleItem>

          <DxSimpleItem data-field="Explicatie" editor-type="dxTextArea"
            :editor-options="{ height: 70, width: '100%' }" />

          <DxSimpleItem data-field="Valoare" editor-type="dxTextBox"
            :editor-options="{ width: '100%' }" />
        </DxForm>

        <div class="footer-fixed footer-modern">
          <div class="footer-right">
            <DxButton text="Salvează" type="success" width="120" height="32" styling-mode="outlined" @click="onSave" />
            <DxButton text="Închide" type="danger" width="120" height="32" styling-mode="outlined"
              @click="isModalVisible = false" />
          </div>
        </div>
      </div>
    </DxPopup>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ODataStore from 'devextreme/data/odata/store';
import AppSettings from '@/settings/appSettings';
import ERP_ParametriService from '@/services/Comercial/ERP_Parametri';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';

import { DxForm, DxSimpleItem, DxRequiredRule } from 'devextreme-vue/form';
import DxPopup from 'devextreme-vue/popup';
import { DxButton } from 'devextreme-vue/button';
import {
  DxDataGrid,
  DxColumn,
  DxFilterRow,
  DxLoadPanel,
  DxPaging,
  DxSearchPanel,
  DxSelection,
  DxSorting,
  DxStateStoring,
  DxToolbar as DxGridToolbar,
  DxItem as DxGridToolbarItem,
  DxEditing,
} from 'devextreme-vue/data-grid';

export default defineComponent({
  name: 'GridERP_Parametri',
  components: {
    DxDataGrid, DxColumn, DxFilterRow, DxLoadPanel,
    DxPaging, DxSearchPanel, DxSelection, DxSorting,
    DxStateStoring, DxGridToolbar, DxGridToolbarItem, DxButton,
    DxEditing, DxPopup, DxForm, DxSimpleItem, DxRequiredRule,
  },
  data() {
    return {
      dsParametri: {} as ODataStore,
      isModalVisible: false,
      isEditMode: false,
      formData: { ID: null as string | null, Cod: '', Nume: '', Explicatie: '' as string | null, Valoare: '' as string | null },
    };
  },
  methods: {
    createODataStore() {
      this.dsParametri = new ODataStore({
        url: AppSettings.API_Url_ComercialOdata + 'ERP_Parametri',
        key: 'ID',
        keyType: 'Guid',
        version: 4,
        beforeSend: (e: any) => {
          e.headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt') };
        },
      });
    },
    onAddClick() {
      this.isEditMode = false;
      this.formData = { ID: null, Cod: '', Nume: '', Explicatie: '', Valoare: '' };
      this.isModalVisible = true;
    },
    onEditClick(data: any) {
      if (!data) return;
      this.isEditMode = true;
      this.formData = { ID: data.ID, Cod: data.Cod, Nume: data.Nume, Explicatie: data.Explicatie ?? '', Valoare: data.Valoare ?? '' };
      this.isModalVisible = true;
    },
    async confirmDelete(data: any) {
      if (!data) return;
      const result = await confirm('Sigur doriți să ștergeți acest parametru?', 'Confirmare Ștergere');
      if (result) {
        try {
          await ERP_ParametriService.delete(data.ID);
          notify('Datele au fost șterse cu succes!', 'success', 1000);
          this.onRefreshClick();
        } catch { notify('Eroare la ștergere!', 'error', 2000); }
      }
    },
    async onSave() {
      const formInstance = (this.$refs.formRef as any)?.instance;
      if (!formInstance?.validate()?.isValid) return;
      try {
        if (this.isEditMode) {
          await ERP_ParametriService.update(this.formData.ID!, {
            Cod: this.formData.Cod,
            Nume: this.formData.Nume,
            Explicatie: this.formData.Explicatie,
            Valoare: this.formData.Valoare,
          });
          notify('Datele au fost actualizate cu succes!', 'success', 1000);
        } else {
          await ERP_ParametriService.save({
            Cod: this.formData.Cod,
            Nume: this.formData.Nume,
            Explicatie: this.formData.Explicatie,
            Valoare: this.formData.Valoare,
          });
          notify('Parametrul a fost adăugat cu succes!', 'success', 1000);
        }
        this.isModalVisible = false;
        this.onRefreshClick();
      } catch (err: any) {
        const msg = err?.response?.data?.message ?? 'Eroare la salvare!';
        notify(msg, 'error', 2000);
      }
    },
    onRefreshClick() {
      this.createODataStore();
      notify('Datele au fost actualizate cu succes!', 'success', 1000);
    },
    onRowRemoving(e: any) { e.cancel = true; },
  },
  mounted() {
    this.createODataStore();
  },
});
</script>

<style scoped>
.grid-border {
  border-radius: 8px;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 18px;
  background: linear-gradient(90deg, #ff2c21, #ff6a4a);
  color: #fff;
  border-radius: 4px 4px 0 0;
}

.popup-header-main {
  display: flex;
  align-items: center;
  gap: 10px;
}

.popup-header-icon {
  font-size: 20px;
}

.popup-header-texts {
  display: flex;
  flex-direction: column;
}

.popup-header-title {
  font-size: 15px;
  font-weight: 700;
}

.popup-header-subtitle {
  font-size: 11px;
  opacity: 0.85;
}

.status-chip {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background-color: rgba(255, 255, 255, 0.18);
}

.popup-body-modern {
  padding: 10px;
}

.footer-fixed {
  margin-top: 20px;
  padding: 12px 0;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #eee;
}

.footer-right {
  display: flex;
  gap: 8px;
}

.grid-action-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  font-size: 18px;
}

.grid-btn-edit {
  color: #1976d2;
  cursor: pointer;
}

.grid-btn-delete {
  color: #d32f2f;
  cursor: pointer;
}

:deep(.dx-button.dx-button-mode-outlined.dx-button-success) {
  border-color: #2e7d32;
  color: #2e7d32;
}

:deep(.dx-button.dx-button-mode-outlined.dx-button-danger) {
  border-color: #d32f2f;
  color: #d32f2f;
}
</style>
